import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  canAccess,
  type AccessModule,
  type AccessPermissions,
  type ProfileLevel,
  type WorkModeView,
} from '@goyone/shared';
import { api, refreshSession, setAccessToken, setSessionLostHandler } from '../lib/api';

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
  coverUrl?: string | null;
  avatarOriginal?: string | null;
  coverOriginal?: string | null;
  phone?: string | null;
  gender?: 'male' | 'female' | 'other' | null;
  birthday?: string | null;
  locale?: string | null;
  /** بريدٌ جديدٌ بانتظار تأكيدٍ برمز — يُعرض للمستخدم أنّه معلّق. */
  pendingEmail?: string | null;
  recoveryEmail?: string | null;
  totpEnabled?: boolean;
  role: 'OWNER' | 'ADMIN' | 'MANAGER' | 'SALES' | 'EMPLOYEE';
  /** دخل بكلمةٍ مؤقّتة — لا شاشةَ قبل أن يضبط كلمتَه. */
  mustChangePassword?: boolean;
}

export interface SessionTenant {
  id: string;
  slug: string;
  name: string;
  status: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'SUSPENDED' | 'CANCELLED';
  trialEndsAt: string | null;
  periodEndsAt: string | null;
  graceEndsAt: string | null;
  planName: string | null;
}

interface Session {
  user: SessionUser;
  tenant: SessionTenant;
  features: string[];
  /** ملفُّ الصلاحيات: وحدةٌ → مستوى. null يعني الدورَ وحدَه. */
  permissions?: AccessPermissions | null;
  /**
   * وضعُ العمل («أشتغل سيلز»): الدورُ الحقيقيّ والملفُّ النشطُ والملفّات.
   * user.role هو الدورُ الفعّال — فـatLeast() وcan() يتبعان الملفَّ النشط.
   */
  workMode?: WorkModeView;
}

/** نتيجةُ الدخول: نجاحٌ فوريّ، أو تحدٍّ على جهازٍ جديد. */
export type LoginResult =
  | { ok: true }
  | { challenge: 'totp' | 'email'; challengeToken: string; email?: string; devCode?: string };

interface AuthValue {
  /** null يعني غير مسجَّل الدخول، وundefined يعني أن الاستعادة لم تنتهِ بعد. */
  session: Session | null | undefined;
  login: (email: string, password: string) => Promise<LoginResult>;
  /** تأكيدُ الدخول من جهازٍ جديد برمزٍ من التطبيق أو البريد. */
  verifyLogin: (challengeToken: string, code: string, trustDevice: boolean) => Promise<void>;
  logout: () => Promise<void>;
  hasFeature: (key: string) => boolean;
  atLeast: (role: SessionUser['role']) => boolean;
  /** إعادةُ قراءة الجلسة بعد تعديلٍ في الاسم أو الصورة — بلا خروجٍ ودخول. */
  reload: () => Promise<void>;
  /** هل يتيح ملفُّ الصلاحيات هذا المستوى على الوحدة؟ — بلا ملفٍّ نعم. */
  can: (module: AccessModule, level?: ProfileLevel) => boolean;
  /** يبدّل الجلسةَ إلى ملفّ عمل، أو (null) يرجعها إلى الدور الحقيقيّ. */
  switchWorkProfile: (id: string | null) => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

const RANK: Record<SessionUser['role'], number> = {
  OWNER: 5,
  ADMIN: 4,
  MANAGER: 3,
  SALES: 2,
  EMPLOYEE: 1,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  const loadProfile = useCallback(async () => {
    const me = await api<Session>('/auth/me');
    setSession(me);
  }, []);

  /*
   * استعادة الجلسة عند الإقلاع.
   *
   * رمز الوصول يعيش في الذاكرة فيضيع مع تحديث الصفحة. كوكي التحديث المحجوبة
   * عن جافاسكريبت تبقى، فنستبدلها برمز جديد. فشل ذلك يعني ببساطة أن المستخدم
   * غير مسجَّل، وليس خطأ يُعرض عليه.
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // عبر الدالة الموحّدة لا بنداء مباشر: نداءان متزامنان يُبطل أحدهما الآخر.
        const ok = await refreshSession();
        if (cancelled) return;
        if (!ok) {
          setSession(null);
          return;
        }
        await loadProfile();
      } catch {
        if (!cancelled) setSession(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadProfile]);

  /* انتهاء الجلسة أثناء الاستخدام يعيد المستخدم إلى شاشة الدخول دون تعليق. */
  useEffect(() => {
    setSessionLostHandler(() => {
      setAccessToken(null);
      setSession(null);
    });
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const res = await api<{
        accessToken?: string;
        challenge?: 'totp' | 'email';
        challengeToken?: string;
        email?: string;
        devCode?: string;
      }>('/auth/login', {
        method: 'POST',
        body: { email, password },
        noRetry: true,
      });
      if (res.challenge && res.challengeToken) {
        return {
          challenge: res.challenge,
          challengeToken: res.challengeToken,
          email: res.email,
          devCode: res.devCode,
        };
      }
      setAccessToken(res.accessToken!);
      await loadProfile();
      return { ok: true };
    },
    [loadProfile],
  );

  /* تأكيدُ الدخول من جهازٍ جديد برمزٍ من التطبيق أو البريد. */
  const verifyLogin = useCallback(
    async (challengeToken: string, code: string, trustDevice: boolean) => {
      const res = await api<{ accessToken: string }>('/auth/login/verify', {
        method: 'POST',
        body: { challengeToken, code, trustDevice },
        noRetry: true,
      });
      setAccessToken(res.accessToken);
      await loadProfile();
    },
    [loadProfile],
  );

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST', noRetry: true });
    } finally {
      // الخروج محليًا يتم في كل الأحوال: فشل الطلب لا يبرر إبقاء الجلسة ظاهرة.
      setAccessToken(null);
      setSession(null);
    }
  }, []);

  /*
   * التبديلُ يُحفظ في الجلسة على الخادم — والخادمُ يفحصه في كلّ طلب — ثمّ
   * تُعاد قراءةُ الجلسة فيتبدّل الدورُ والصلاحياتُ في الواجهة كلّها.
   */
  const switchWorkProfile = useCallback(
    async (id: string | null) => {
      await api('/auth/work-profile/switch', { method: 'POST', body: { id } });
      await loadProfile();
    },
    [loadProfile],
  );

  const value = useMemo<AuthValue>(
    () => ({
      session,
      login,
      verifyLogin,
      logout,
      hasFeature: (key) => !!session?.features.includes(key),
      atLeast: (role) => !!session && RANK[session.user.role] >= RANK[role],
      reload: loadProfile,
      can: (module, level = 'VIEW') => canAccess(session?.permissions ?? null, module, level),
      switchWorkProfile,
    }),
    [session, login, verifyLogin, logout, loadProfile, switchWorkProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth يجب أن يُستخدم داخل AuthProvider.');
  return ctx;
}

/** الجلسة مع ضمان وجودها — للاستخدام داخل الشاشات المحمية. */
export function useSession(): Session {
  const { session } = useAuth();
  if (!session) throw new Error('لا توجد جلسة نشطة.');
  return session;
}
